import { useEffect, useState } from 'react'

import { useFilters, useFiltersActions } from '../context'
import { fromParams, getParams, toParams } from '../storageOptions'

/**
 * Binds the filter store to the current page's URL query string. Should be
 * called from the route that owns the filter UI (ProjectOverviewPage). Other
 * routes deliberately do not call this so they never write filter params,
 * even when the filter store still has filters from a previous page.
 *
 * Behavior:
 *   - On mount, reads filter params from the URL and hydrates the store.
 *   - When filters change, mirrors them back into the URL.
 *   - On unmount, strips filter params from the URL so navigating to a
 *     sibling route ('/files', '/insights', ...) lands on a clean URL.
 *
 * Uses `history.replaceState` directly to avoid round-tripping through the
 * router, matching the pattern in `createLocalAndUrlStorage`.
 */
export const useFiltersUrlSync = (): void => {
  const filters = useFilters()
  const { setFilters } = useFiltersActions()
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search.slice(1))
    const fromUrl = fromParams(params)
    if (fromUrl?.filters?.length) {
      setFilters(fromUrl.filters)
    }
    setHydrated(true)
  }, [setFilters])

  useEffect(() => {
    if (!hydrated) return
    writeFilterParams(filters)
  }, [filters, hydrated])

  useEffect(() => {
    return () => {
      stripFilterParams()
    }
  }, [])
}

function writeFilterParams(filters: ReturnType<typeof useFilters>) {
  const params = new URLSearchParams(window.location.search.slice(1))
  getParams(params).forEach((key) => params.delete(key))

  const next = toParams({ filters, dateRanges: {}, searchQuery: '' })
  if (next) {
    Object.entries(next).forEach(([key, value]) => params.set(key, value))
  }

  replaceUrlSearch(params)
}

function stripFilterParams() {
  const params = new URLSearchParams(window.location.search.slice(1))
  const keys = getParams(params)
  if (keys.length === 0) return
  keys.forEach((key) => params.delete(key))
  replaceUrlSearch(params)
}

function replaceUrlSearch(params: URLSearchParams) {
  const query = params.toString()
  const next = `${window.location.pathname}${query ? `?${query}` : ''}`
  window.history.replaceState(null, '', next)
}
